import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import InseeSearch from '@/components/InseeSearch';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Users, Plus, Search, Loader2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', siret: '', siren: '', pays_iso: 'FR', tva_intracommunautaire: '', conditions_paiement: '30 jours' });

  const fetch = async () => {
    try {
      const res = await axios.get(`${API_URL}/clients`, { params: search ? { search } : {} });
      setClients(res.data);
    } catch (e) { toast.error('Erreur chargement clients'); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); fetch(); }, [search]);

  const handleSave = async () => {
    if (!form.nom) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/clients/${editing}`, form);
        toast.success('Client modifié');
      } else {
        await axios.post(`${API_URL}/clients`, form);
        toast.success('Client créé');
      }
      setCreateOpen(false);
      setEditing(null);
      setForm({ nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', siret: '', siren: '', pays_iso: 'FR', tva_intracommunautaire: '', conditions_paiement: '30 jours' });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ nom: c.nom, email: c.email || '', telephone: c.telephone || '', adresse: c.adresse || '', code_postal: c.code_postal || '', ville: c.ville || '', siret: c.siret || '', siren: c.siren || '', pays_iso: c.pays_iso || 'FR', tva_intracommunautaire: c.tva_intracommunautaire || '', conditions_paiement: c.conditions_paiement || '30 jours' });
    setCreateOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white"><Users className="w-6 h-6 inline mr-2" />Clients</h1>
          <p className="text-slate-500">{clients.length} client(s)</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', siret: '', siren: '', pays_iso: 'FR', tva_intracommunautaire: '', conditions_paiement: '30 jours' }); setCreateOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]">
          <Plus className="w-4 h-4 mr-2" /> Nouveau client
        </Button>
      </div>

      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input placeholder="Rechercher..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} /></div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-24">Code</TableHead><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Ville</TableHead><TableHead className="w-20">Factures</TableHead><TableHead className="w-28">Total HT</TableHead><TableHead className="w-24"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {clients.map(c => (
                <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => openEdit(c)}>
                  <TableCell className="font-mono text-sm">{c.code}</TableCell>
                  <TableCell className="font-medium">{c.nom}</TableCell>
                  <TableCell className="text-sm">{c.email || '-'}</TableCell>
                  <TableCell className="text-sm">{c.ville || '-'}</TableCell>
                  <TableCell><Badge variant="outline">{c.invoice_count || 0}</Badge></TableCell>
                  <TableCell className="font-mono">{formatCurrency(c.total_ht || 0)}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(c); }}>Modifier</Button></TableCell>
                </TableRow>
              ))}
              {clients.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">Aucun client</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Modifier le client' : 'Nouveau client'}</DialogTitle></DialogHeader>
          {!editing && (
            <div className="mb-2">
              <Label className="text-sm text-slate-500">Rechercher dans le répertoire INSEE</Label>
              <InseeSearch onSelect={(company) => setForm(prev => ({
                ...prev,
                nom: company.nom_complet || company.nom || prev.nom,
                siret: company.siret || prev.siret,
                siren: company.siret ? company.siret.slice(0, 9) : (company.siren || prev.siren),
                adresse: company.adresse || prev.adresse,
                code_postal: company.code_postal || prev.code_postal,
                ville: company.ville || prev.ville,
                tva_intracommunautaire: company.tva_intracommunautaire || prev.tva_intracommunautaire,
              }))} placeholder="Nom ou SIREN du client..." />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Nom / Raison sociale *</Label><Input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            <div><Label>Téléphone</Label><Input value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} /></div>
            <div className="col-span-2"><Label>Adresse</Label><Input value={form.adresse} onChange={e => setForm({...form, adresse: e.target.value})} /></div>
            <div><Label>Code postal</Label><Input value={form.code_postal} onChange={e => setForm({...form, code_postal: e.target.value})} /></div>
            <div><Label>Ville</Label><Input value={form.ville} onChange={e => setForm({...form, ville: e.target.value})} /></div>
            <div><Label>SIRET (14 chiffres)</Label><Input value={form.siret} onChange={e => setForm({...form, siret: e.target.value, siren: e.target.value.slice(0, 9)})} placeholder="12345678901234" /></div>
            <div><Label>SIREN (9 chiffres)</Label><Input value={form.siren} onChange={e => setForm({...form, siren: e.target.value})} placeholder="Auto-calculé depuis SIRET" className="bg-slate-50 dark:bg-slate-800" /></div>
            <div><Label>Pays (ISO)</Label>
              <select className="w-full border rounded-md px-3 py-2 text-sm dark:bg-slate-800 dark:border-slate-600" value={form.pays_iso} onChange={e => setForm({...form, pays_iso: e.target.value})}>
                <option value="FR">FR - France</option><option value="DE">DE - Allemagne</option><option value="BE">BE - Belgique</option><option value="ES">ES - Espagne</option><option value="IT">IT - Italie</option><option value="LU">LU - Luxembourg</option><option value="CH">CH - Suisse</option><option value="GB">GB - Royaume-Uni</option><option value="US">US - États-Unis</option><option value="NL">NL - Pays-Bas</option><option value="PT">PT - Portugal</option><option value="AT">AT - Autriche</option>
              </select>
            </div>
            <div><Label>TVA intracommunautaire</Label><Input value={form.tva_intracommunautaire} onChange={e => setForm({...form, tva_intracommunautaire: e.target.value})} placeholder="FR12345678901" /></div>
            <div><Label>Conditions de paiement</Label><Input value={form.conditions_paiement} onChange={e => setForm({...form, conditions_paiement: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
